import React, { useEffect, useState } from "react";
import { Box, Paper, Grid, TextField, MenuItem, Button, Snackbar, Alert } from "@mui/material";
import { useMasterData } from "../../../hooks/useMasterData";
import { gatePassApi } from "./gatePassApi";
import GatePassForm from "./GatePassForm";
import GatePassTable from "./GatePassTable";

const GatePassPage: React.FC = () => {
  const { masters, fetchMasters, fetchAcademicSessions, fetchSections } = useMasterData();
  const [filters, setFilters] = useState<{ sessionId?: string; classId?: string; sectionId?: string; status?: string; dateFrom?: string; dateTo?: string; studentId?: string }>({});
  const [sections, setSections] = useState<{ id: string; name: string }[]>([]);
  const [students, setStudents] = useState<Array<any>>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({ open: false, message: "", severity: "info" });
  const nowIST = () => {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    const ist = new Date(utcMs + 5.5 * 3600000);
    const y = ist.getFullYear();
    const m = String(ist.getMonth() + 1).padStart(2, "0");
    const d = String(ist.getDate()).padStart(2, "0");
    const hh = String(ist.getHours()).padStart(2, "0");
    const mm = String(ist.getMinutes()).padStart(2, "0");
    return `${y}-${m}-${d}T${hh}:${mm}`;
  };
  const [form, setForm] = useState<{ studentId: string; reason: string; exitTime: string; expectedReturnTime?: string; approvedBy?: string }>({
    studentId: "",
    reason: "",
    exitTime: nowIST(),
  });

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, []);

  useEffect(() => {
    if (filters.classId) {
      fetchSections(filters.classId).then(setSections);
    } else {
      setSections([]);
      setFilters((p) => ({ ...p, sectionId: "" }));
    }
  }, [filters.classId]);

  const loadStudents = async () => {
    try {
      const res = await (await import("../certificates/certificateApi")).certificateApi.students({
        sessionId: filters.sessionId,
        classId: filters.classId,
        sectionId: filters.sectionId,
        search: filters.studentId ? filters.studentId : "",
      });
      setStudents(res);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load students", severity: "error" });
    }
  };

  const search = async () => {
    try {
      const res = await gatePassApi.list(filters);
      setRows(res);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load gate passes", severity: "error" });
    }
  };

  const resetFilters = () => {
    setFilters({});
    setRows([]);
  };

  const issuePass = async () => {
    try {
      await gatePassApi.create(form);
      setSnackbar({ open: true, message: "Gate pass issued", severity: "success" });
      setForm({ studentId: "", reason: "", exitTime: new Date().toISOString().slice(0, 16) });
      search();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to issue gate pass", severity: "error" });
    }
  };

  const markReturned = async (row: any) => {
    try {
      await gatePassApi.markReturned(row.id);
      setSnackbar({ open: true, message: "Gate pass marked returned", severity: "success" });
      search();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to mark returned", severity: "error" });
    }
  };

  const cancel = async (row: any) => {
    try {
      await gatePassApi.cancel(row.id);
      setSnackbar({ open: true, message: "Gate pass cancelled", severity: "success" });
      search();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to cancel pass", severity: "error" });
    }
  };

  const printPass = (row: any) => {
    const w = window.open("", "_blank", "width=800,height=600");
    if (!w) return;
    w.document.write(`
      <html>
        <head>
          <title>${row.passNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .title { text-align: center; font-size: 18px; font-weight: 700; margin-bottom: 12px; }
            .row { margin: 6px 0; }
            .label { font-weight: 600; width: 180px; display: inline-block; }
          </style>
        </head>
        <body>
          <div class="title">Gate Pass</div>
          <div class="row"><span class="label">School Name:</span> ${row.student?.school?.name || ""}</div>
          <div class="row"><span class="label">Gate Pass Number:</span> ${row.passNumber}</div>
          <div class="row"><span class="label">Student Name:</span> ${row.student ? `${row.student.firstName} ${row.student.lastName}` : ""}</div>
          <div class="row"><span class="label">Class:</span> ${row.student?.class?.name || ""}</div>
          <div class="row"><span class="label">Section:</span> ${row.student?.section?.name || ""}</div>
          <div class="row"><span class="label">Reason:</span> ${row.reason || ""}</div>
          <div class="row"><span class="label">Exit Time:</span> ${new Date(row.exitTime).toLocaleString()}</div>
          <div class="row"><span class="label">Signature:</span> ___________________________</div>
          <script>window.print();</script>
        </body>
      </html>
    `);
    w.document.close();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ fontSize: 22, fontWeight: 600, mb: 2 }}>Student Gate Pass</Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField select fullWidth label="Session" value={filters.sessionId || ""} onChange={(e) => setFilters({ ...filters, sessionId: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {masters.academicSessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth label="Class" value={filters.classId || ""} onChange={(e) => setFilters({ ...filters, classId: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {masters.classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Section"
              value={filters.sectionId || ""}
              disabled={!filters.classId}
              onChange={(e) => setFilters({ ...filters, sectionId: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {sections.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth label="Status" value={filters.status || ""} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ISSUED">ISSUED</MenuItem>
              <MenuItem value="RETURNED">RETURNED</MenuItem>
              <MenuItem value="OVERDUE">OVERDUE</MenuItem>
              <MenuItem value="CANCELLED">CANCELLED</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="From" type="date" fullWidth value={filters.dateFrom || ""} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="To" type="date" fullWidth value={filters.dateTo || ""} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" onClick={search}>
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth onClick={resetFilters}>Reset</Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={loadStudents}>Load Students</Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <GatePassForm students={students} form={form} onChange={setForm} onSubmit={issuePass} onCancel={() => setForm({ studentId: "", reason: "", exitTime: nowIST() })} />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <GatePassTable rows={rows} onView={() => {}} onReturn={markReturned} onCancel={cancel} onPrint={printPass} />
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GatePassPage;
